/**
 * Route guard for the marketing site's signed-in pages. Its only use today is
 * `pages/tax-exemption/index.tsx`, with `Permission.Tax_Exempt_Submit`.
 *
 * In order:
 * 1. Signed out: saves the current path and hash in sessionStorage `redirectTo` and replaces the
 *    location with `/login`. The query string is not saved. After sign-in,
 *    `components/Login/LoginHelpers/LoginForm.tsx` → `LoginForm` sends the user to `redirectTo`,
 *    and `components/Login/hooks/useRedirectIfLoggedIn.ts` does the same for a visitor who is
 *    already signed in.
 * 2. Account `"99"` asking for a page guarded by `Customer_Dashboard_View_Edit`: sent to `/`.
 *    Account 99 is Ryerson's own company; its users are Ryerson sales reps, kept off the customer
 *    dashboard. This branch doesn't return, so `component` still renders until the navigation
 *    lands. No page here passes that permission, so the branch is not reached today.
 * 3. Missing the page's `permission`: sent to `/store/dashboard` when the user can see the
 *    dashboard, otherwise to `/`. `/store/dashboard` is a store page: `pages/_app.tsx` → `App`
 *    turns the client-side navigation into a full reload so CloudFront serves web-store-ui.
 * 4. Otherwise: records the last path segment in sessionStorage `pageLocation` and renders
 *    `component`. No file in this repository reads `pageLocation`; web-store-ui's
 *    `components/Shared/MyAccountDashboardMenu.tsx` reads the same key.
 *
 * Rules a change must keep:
 * - **The `redirectTo` hand-off is shared with web-store-ui** (web-architecture ADR-0035). Three
 *   writers save the return path in sessionStorage `redirectTo` and send the visitor to `/login`:
 *   - this guard and `web-store-ui/application/src/components/NextLayouts/ProtectedRoute.tsx`,
 *     which save the path and hash, then call `window.location.replace("/login")`;
 *   - `@ryerson/frontend.header-footer` → `Renderers/LoginButtonRenderer.tsx` → `LoginButton`,
 *     the header's sign-in button. On a touch device, when signed out, it saves
 *     `window.location.pathname` (no hash), then sets `window.location.href = "/login"`.
 *   The readers are `LoginForm` and `useRedirectIfLoggedIn` (above). The key name, the `/login`
 *   path and the value's shape (a site-root path) must stay the same across the three writers and
 *   the two readers. `@ryerson/frontend.application` → `redirectToLogin`, used on session expiry,
 *   logs out and goes to `/login` without saving a return path.
 * - This is a UI guard only. Enforcing `permission` is the job of the API a page calls, and not
 *   every API route behind a guarded page checks it. `/tax-exemption` posts through
 *   `components/ContactFormWithFileAttacher/index.tsx` to web-common-api's public contact-us
 *   route (`/v1/common/v2/public/contact-us`), which does not check `Tax_Exempt_Submit`. That is
 *   accepted: a submission is an email to the tax team, and the permission only decides who sees
 *   the form.
 *
 * It reads `window` and `sessionStorage` during render. That is safe because pages render only in
 * the browser (`RootLayout.tsx` → `HasMounted`).
 *
 * Related: `PublicWithFeature.tsx`, the guard for public pages behind a feature toggle.
 */
import React, { FunctionComponent } from "react";
import { navigate } from "@ryerson/frontend.next";
import { Permission } from "@ryerson/common.permissions";
import { useApplication } from "@ryerson/frontend.application";

type RouteProps = {
	component?: FunctionComponent<any>;
	// Checked with `hasPermission`; omitted means any signed-in user.
	permission?: Permission;
	// Spread onto `component` when it renders.
	props?: any;
};

/**
 * Renders `component` with `props` for a signed-in user who holds `permission`, and otherwise
 * redirects; see the file header for each case.
 */
export const ProtectedRoute: React.FC<RouteProps> = ({
	component: Component,
	permission,
	props = {},
}) => {
	const { hash = "", pathname = "" } = window?.location || {};
	const urlHash = (typeof window !== "undefined" && hash) || "";
	const redirectTo = `${pathname}${urlHash}`;

	const {
		user: { isLoggedIn, account },
		hasPermission,
	} = useApplication();

	 
	if (!isLoggedIn) {
		sessionStorage.setItem("redirectTo", redirectTo);
		window?.location.replace("/login");
		return null;
	} else {
		if (account === "99" && permission === Permission.Customer_Dashboard_View_Edit) {
			// Attempt to stop navigation to dashboard for company 99 users.
			// No `return`: `component` still renders until the navigation lands.
			navigate("/");
		} else if (permission && !hasPermission(permission)) {
			hasPermission(Permission.Customer_Dashboard_View_Edit)
				? navigate("/store/dashboard")
				: navigate("/");
			return null;
		}

		if (sessionStorage) {
			const currWindowPath = pathname.split("/").pop() || "";
			currWindowPath && sessionStorage.setItem("pageLocation", currWindowPath);
		}
		return Component ? <Component {...props} /> : <></>;
	}
};
